// ─── Enums / Unions ─────────────────────────────────────────────────────────────

export type Industry =
  | 'healthcare' | 'general' | 'legal' | 'financial' | 'food_service'
  | 'real_estate' | 'education' | 'technology' | 'ecommerce' | 'automotive'
  | 'construction' | 'beauty_wellness' | 'nonprofit' | 'professional_services'
  | 'fitness' | 'travel_hospitality' | 'home_services' | 'manufacturing'
  | 'agriculture' | 'media_entertainment'

export type ContentType =
  | 'facebook_post' | 'twitter_post' | 'instagram_post' | 'linkedin_post'
  | 'blog_seo' | 'email_outreach' | 'facebook_ad' | 'instagram_ad'
  | 'google_ads' | 'gbp_post'

export const ALL_CONTENT_TYPES = [
  'facebook_post', 'twitter_post', 'instagram_post', 'linkedin_post',
  'blog_seo', 'email_outreach', 'facebook_ad', 'instagram_ad',
  'google_ads', 'gbp_post',
] as const satisfies ContentType[]

export type LegacyContentType = 'social_media' | 'ad_copy'
export type AnyContentType = ContentType | LegacyContentType

export type RetrieverProvider = 'elastic' | 'elastic-local' | 'pinecone' | 'mongodb'

export type PlatformName =
  | 'facebook' | 'twitter' | 'medium' | 'google' | 'sendgrid'
  | 'instagram' | 'linkedin' | 'gmail' | 'outlook'

export type PublishStatus =
  | 'draft' | 'scheduled' | 'pending' | 'waiting_approval' | 'publishing'
  | 'published' | 'failed' | 'rejected' | 'approved'

export type AgentStatus = 'idle' | 'running' | 'publishing' | 'complete' | 'error' | 'stopped'
export type NodeStatus = 'pending' | 'active' | 'done' | 'error'
export type ContentTypeStatus = 'pending' | 'generating' | 'done'
export type BuyerJourneyStage = 'awareness' | 'consideration' | 'decision'
export type PlanType = 'free' | 'pro' | 'enterprise'
export type ReportType = 'daily' | 'weekly'
export type MemoryType = 'experiment' | 'insight' | 'failure' | 'audience_signal'
export type ReviewReplyStatus = 'pending' | 'auto_replied' | 'needs_human' | 'replied'
export type CustomerEquipmentStatus = 'active' | 'returned' | 'extended'
export type ReferralPartnerType =
  | 'hospital' | 'home_health' | 'hospice' | 'case_manager' | 'therapy' | 'other'

export type NodeName =
  | 'resolve_client' | 'persona_builder' | 'strategy_planner'
  | 'campaign_writer' | 'tone_checker' | 'ethics_guardrail' | 'output_formatter'
  | 'historical_context' | 'increment_retry' | string

export type NotificationChannel = 'whatsapp' | 'telegram'
export type ScheduleCadence = 'daily' | 'weekly' | 'custom_cron'
export type ContentSource = 'auto' | 'manual'

// ─── Knowledge ──────────────────────────────────────────────────────────────────────────

export interface KnowledgeFileMeta {
  filename: string
  size: number
  type: string
  chunks: number
  uploaded_at: string
}

// ─── Platform Credentials ────────────────────────────────────────────────────────────────

export interface FacebookCredentials {
  platform: 'facebook'
  page_id: string; page_name: string; page_access_token: string
  user_access_token?: string; token_expires_at?: number
  connected_at: string; needs_page?: boolean
}
export interface TwitterCredentials {
  platform: 'twitter'
  user_id: string; username: string
  access_token: string; refresh_token: string; token_expires_at: number
  connected_at: string
}
export interface MediumCredentials {
  platform: 'medium'
  author_id: string; username: string; name: string
  access_token: string; connected_at: string
}
export interface GoogleCredentials {
  platform: 'google'
  access_token: string; refresh_token: string; token_expires_at: number
  connected_at: string; scopes: string[]
  ads_customer_id?: string; gbp_location_id?: string; ga4_property_id?: string
  search_console_site_url?: string; blogger_blog_id?: string
  blogger_blog_name?: string; blogger_blog_url?: string
}
export interface SendGridCredentials {
  platform: 'sendgrid'
  api_key: string; from_email: string; from_name?: string
  reply_to?: string; connected_at: string
}
export interface InstagramCredentials {
  platform: 'instagram'
  ig_user_id: string; ig_username: string; page_id: string
  page_access_token: string; token_expires_at?: number; connected_at: string
}
export interface LinkedInCredentials {
  platform: 'linkedin'
  person_id?: string; organization_id?: string; organization_name?: string
  access_token: string; refresh_token?: string; token_expires_at: number
  connected_at: string; post_as: 'person' | 'organization'
}
export interface GmailCredentials {
  platform: 'gmail'
  access_token: string; refresh_token: string; token_expires_at: number
  email: string; connected_at: string; scopes: string[]
}
export interface OutlookCredentials {
  platform: 'outlook'
  access_token: string; refresh_token: string; token_expires_at: number
  email: string; connected_at: string; scopes: string[]
}

export type PlatformCredentials =
  | FacebookCredentials | TwitterCredentials | MediumCredentials
  | GoogleCredentials | SendGridCredentials | InstagramCredentials
  | LinkedInCredentials | GmailCredentials | OutlookCredentials

export type PlatformConnections = Partial<Record<PlatformName, PlatformCredentials>>

// ─── Client Config ───────────────────────────────────────────────────────────────────────

export interface ContentInclusionSettings {
  include_address: boolean; include_phone: boolean; include_email: boolean
  include_website: boolean; include_pricing: boolean; include_staff_names: boolean
}
export interface MarketingProfile {
  phone?: string; cta_url?: string; campaign_goal?: string
  gbp_review_count?: number; gbp_rating?: number
  facebook_url?: string; linkedin_url?: string; twitter_url?: string
}
export interface ClientCapabilities {
  businessDescription?: string; productCategories?: string[]
  servicesOffered?: string[]; acceptedInsurance?: string[]
  serviceArea?: string; deliveryTimeline?: string; ehrIntegrations?: string[]
}
export interface CallRailConfig {
  access_token?: string; refresh_token?: string; token_expires_at?: number
  connected_at?: string; account_id: string; account_name?: string
  company_id: string; company_name?: string; api_key?: string
}
export interface TwilioConfig {
  account_sid: string; auth_token: string; phone_number: string
}
export interface WebhookConfig { url: string; enabled: boolean }

// ─── Notifications ──────────────────────────────────────────────────────────────────────

export interface NotificationRecipient {
  id: string
  channel: NotificationChannel
  label?: string
  phone?: string
  chat_id?: string
  verified_at?: string
  added_at: string
}

// Shape returned by GraphQL (camelCase). Mirrors backend `NotificationRecipient` type.
export interface NotificationRecipientGql {
  id: string
  channel: NotificationChannel
  label?: string | null
  phone?: string | null
  chatId?: string | null
  verifiedAt?: string | null
  addedAt: string
}

// Input shape for `setNotificationRecipients` mutation.
export interface NotificationRecipientInput {
  id?: string
  channel: NotificationChannel
  label?: string
  phone?: string
  chatId?: string
}

export interface NotificationTestResult {
  ok: boolean
  providerId?: string | null
  error?: string | null
}

export interface WhatsappVerificationInit {
  code: string
  waLink: string
  phoneNumber: string
  expiresIn: number
}

export interface TelegramConnectionInit {
  code: string
  botUrl: string
  botUsername: string
  expiresIn: number
}

export const MAX_RECIPIENTS_PER_CHANNEL = 3 as const

// ─── Scheduling ──────────────────────────────────────────────────────────────────────────

export interface AutoGenerateConfig {
  enabled: boolean
  content_types: ContentType[]
  cadence: ScheduleCadence
  time_utc: string
  days_of_week?: number[]
  cron_expr?: string
  timezone?: string
}

export interface SchedulingConfig {
  auto_generate?: AutoGenerateConfig
  notify_on_queue?: boolean
  last_auto_run_at?: string
  next_auto_run_at?: string
}

export interface ContentPillar {
  id: string
  name: string
  description?: string
}

export interface ClientProduct {
  id: string; name: string; cms_category?: string
  description?: string; rank: number; is_flagship: boolean
}

export interface ClientConfig {
  id: string; owner_uid: string; user_id: string
  brand_name: string; brandName?: string; brand_location: string; brandLocation?: string
  brand_specialization: string; brandSpecialization?: string
  industry: Industry; retriever_provider: RetrieverProvider
  agent_name?: string; agentName?: string; agent_description?: string; compliance_prompt?: string
  content_inclusion?: ContentInclusionSettings
  created_at: string; knowledge_files: string[]; knowledge_files_meta?: KnowledgeFileMeta[]
  webhook_config?: WebhookConfig; platform_connections?: PlatformConnections
  selected_content_types?: ContentType[]; selectedContentTypes?: ContentType[]
  seo_keywords?: string; website_url?: string; websiteUrl?: string
  lead_tracking_api_key?: string
  model_provider?: string; model_id?: string; provider_api_key?: string
  model_creative?: string; model_analytical?: string; model_mechanical?: string
  model_compliance?: string; model_response?: string; model_query?: string
  auto_run?: boolean; auto_publish?: boolean
  budget_limit_daily_usd?: number; budget_limit_monthly_usd?: number
  budgetLimitDailyUsd?: number
  notification_email?: string; notification_phone?: string; sms_alerts?: boolean
  notification_webhook?: string; scheduling_config?: SchedulingConfig
  content_pillars?: ContentPillar[]
  products?: ClientProduct[]; callrail_config?: CallRailConfig
  twilio_config?: TwilioConfig; sendgrid_api_key?: string
  notification_from_email?: string; disabled_at?: string
  marketing_profile?: MarketingProfile
  capabilities?: ClientCapabilities
  // camelCase GraphQL response fields (returned by GET_CLIENT query)
  marketingProfile?: {
    phone?: string; ctaUrl?: string; campaignGoal?: string
    gbpReviewCount?: number; gbpRating?: number
    facebookUrl?: string; linkedinUrl?: string; twitterUrl?: string
  }
  contentInclusion?: {
    includeAddress?: boolean; includePhone?: boolean; includeEmail?: boolean
    includeWebsite?: boolean; includePricing?: boolean; includeStaffNames?: boolean
  }
  schedulingConfig?: {
    autoGenerate?: {
      enabled: boolean
      contentTypes: ContentType[]
      cadence: ScheduleCadence
      timeUtc: string
      daysOfWeek?: number[] | null
      cronExpr?: string | null
      timezone?: string | null
    } | null
    notifyOnQueue?: boolean | null
    lastAutoRunAt?: string | null
    nextAutoRunAt?: string | null
  } | null
}

// ─── Runs ────────────────────────────────────────────────────────────────────────────

export interface TokenUsage {
  input_tokens: number; output_tokens: number; total_tokens: number
}

// Camelcase shape returned by the GET_RUNS GraphQL query via Apollo.
export interface TokenUsageGql {
  inputTokens: number; outputTokens: number; totalTokens: number
}
export interface RunSummaryGql {
  id: string
  clientId: string
  startedAt: string
  completedAt: string | null
  status: 'running' | 'complete' | 'error' | 'stopped'
  resultsCount: number
  tokenUsage: TokenUsageGql | null
  estimatedCostUsd: number | null
  engine: string | null
  selectedContentTypes: ContentType[] | null
  errorMessage: string | null
  trigger: string | null
}

export interface RunSummary {
  _id: string; client_id: string; started_at: string; completed_at?: string | null
  status: 'running' | 'complete' | 'error' | 'stopped'
  results_count: number; token_usage: TokenUsage
  estimated_cost_usd: number; error_message?: string | null
  selected_content_types?: ContentType[]
  selectedContentTypes?: ContentType[]
}
export interface RunEvent {
  type: 'node_complete' | 'error'; node: string
  message: string; timestamp: string; data?: unknown
}
export interface RunLogEntry {
  id: string; message: string
  type: 'info' | 'success' | 'error' | 'progress'; timestamp: string
}
export interface RunNodeDetail { summary: string; details?: string; type?: string }
export interface NodeProgress {
  name: string
  status: 'pending' | 'running' | 'complete' | 'error'
  completedAt: string | null
  data: Record<string, unknown> | null
}
export interface RunDetail extends RunSummary {
  campaign_plan: string
  results: AutonomousResult[]
  events: RunEvent[]
  node_statuses?: Record<NodeName, NodeStatus>
  active_node?: NodeName | string
  node_details?: Record<string, RunNodeDetail>
  content_type_statuses?: Record<string, ContentTypeStatus>
  log_entries?: RunLogEntry[]
  node_runtime_data?: Record<string, unknown>
  active_workflow_node_type?: string
  graphNodes?: string[]
  completedNodes?: string[]
  progressPct?: number
  formattedOutput?: Record<string, unknown> | null
  nodeProgress?: NodeProgress[]
  errorMessage?: string | null
}

// ─── Publish Queue ────────────────────────────────────────────────────────────────────

export interface PlatformPublishResult {
  platform: PlatformName; success: boolean
  platform_post_id?: string; platform_url?: string
  error?: string; updatedCredentials?: Partial<PlatformCredentials>
}
export interface SeoMetadata { meta_title: string; meta_description: string; slug: string }
export interface ComplianceResult {
  approved: boolean; issues: string[]; suggested_fixes: string[]
}

export interface PublishQueueItem {
  _id: string; client_id: string; run_id: string
  content_type: AnyContentType; content: string; subject?: string
  compliance: ComplianceResult; status: PublishStatus
  approved_at?: string; published_at?: string
  webhook_response?: unknown; error_message?: string; rejected_feedback?: string
  created_at: string; publish_results?: PlatformPublishResult[]
  published_to?: PlatformName[]; seo_metadata?: SeoMetadata
  scheduled_for?: string; tracking_url?: string; click_count?: number
  target_platform?: PlatformName; repurposed_from?: string; image_url?: string
  recipient_npi?: string; recipient_name?: string; recipient_practice?: string
  recipient_channel?: string; to_email?: string; outreach_type?: string
  talking_points?: string[]; platform_config?: Record<string, unknown>
  dme_revision_count?: number; dme_compliance_result?: ComplianceResult
  node_runtime_data?: Record<string, unknown>
  source?: ContentSource
}

// Camelcase shape returned by mapQueueItem in the backend resolver (what Apollo actually delivers)
export interface GqlPublishQueueItem {
  id: string
  clientId: string
  runId?: string
  contentType?: string
  content: string
  subject?: string
  status: string
  createdAt: string
  updatedAt?: string
  approvedAt?: string
  publishedAt?: string
  scheduledFor?: string
  errorMessage?: string
  rejectedFeedback?: string
  toEmail?: string
  recipientName?: string
  recipientNpi?: string
  recipientPractice?: string
  recipientChannel?: string
  outreachType?: string
  targetPlatform?: string
  imageUrl?: string
  talkingPoints?: string[]
  trackingUrl?: string
  clickCount?: number
  publishedTo?: string[]
  compliance?: { approved: boolean; issues: string[]; suggestedFixes: string[] } | null
  publishResults?: Array<{
    platform: string; success: boolean; platformPostId?: string
    platformUrl?: string; error?: string; publishedAt?: string
  }>
  seoMetadata?: { metaTitle?: string; metaDescription?: string; slug?: string } | null
}

// ─── Generation ─────────────────────────────────────────────────────────────────────────

export interface CheckResult {
  type: string; passed: boolean; issues: string[]
  suggested_fixes?: string[]; revision_count?: number
}
export interface AutonomousResult {
  content_type: ContentType; content: string
  compliance: ComplianceResult; revision_count: number; seo_metadata?: SeoMetadata
}
export interface StreamPublishQueueItem {
  slot_id: string
  channel: string
  content: string
  status: string
  cta?: string
  hashtags?: string[]
  scheduled_time?: string | null
  content_pillar?: string
  buyer_journey_stage?: string
  title?: string
  meta_description?: string
  slug?: string
}
export interface StreamEvent {
  type: 'node_start' | 'node_end' | 'error' | 'done'
  node?: NodeName; data?: unknown; timestamp: string
}
export interface ChatMessage { role: 'user' | 'assistant'; content: string }

// ─── Analytics ──────────────────────────────────────────────────────────────────────────

export interface Lead {
  _id: string; client_id: string; lead_name: string; lead_email: string
  lead_phone?: string; lead_message?: string; source_platform: PlatformName
  source_content_type: AnyContentType; source_run_id: string
  source_queue_item_id: string; metadata?: unknown; created_at: string
}
export interface LeadAnalytics {
  total_leads: number; leads_this_month: number; total_clicks: number
  leads_by_content_type: Array<{ content_type: AnyContentType; count: number }>
  leads_by_platform: Array<{ platform: PlatformName; count: number }>
  recent_leads: Lead[]
  top_content_pieces: Array<{ item_id: string; clicks: number; leads: number }>
}
export interface PerformanceSnapshot {
  _id?: string; client_id: string; source: string; snapshot_date: string
  metrics: {
    impressions?: number; clicks?: number; conversions?: number
    cost_usd?: number; cpa_usd?: number; ctr?: number; roas?: number
    sessions?: number; bounce_rate?: number; avg_session_duration?: number
    avg_position?: number; calls?: number; first_time_callers?: number
    avg_call_duration?: number; post_views?: number; reactions?: number
    engaged_users?: number; shares?: number; comments?: number; page_followers?: number
  }
  breakdown?: unknown; created_at: string
}

// ─── Account / Plan ───────────────────────────────────────────────────────────────────

export interface UserAccount {
  uid: string; email: string; display_name?: string; displayName?: string
  plan_type: PlanType; planType?: PlanType; max_clients: number; maxClients?: number
  created_at: string; createdAt?: string; updated_at: string; updatedAt?: string
  photoUrl?: string | null
  brand_name?: string; brand_location?: string; brand_specialization?: string
  industry?: Industry; website_url?: string
  platform_connections?: PlatformConnections
  platforms?: Array<{ platform: string; connected: boolean; connectedAt: string | null; email?: string | null }>
  defaultEmailProvider?: string | null
  clientCount?: number; runsThisMonth?: number; disabledAt?: string | null
  callrail_config?: CallRailConfig; twilio_config?: TwilioConfig
  sendgrid_api_key?: string; notification_email?: string; notification_phone?: string
  sms_alerts?: boolean; whatsapp_alerts?: boolean; whatsapp_verified?: boolean
  whatsapp_connected_at?: string; notification_webhook?: string
  notification_from_email?: string; slack_webhook_url?: string
  slack_channel_name?: string; telegram_chat_id?: string; telegram_connected_at?: string
  notification_recipients?: NotificationRecipient[]
  notificationRecipients?: NotificationRecipientGql[]
  auto_publish?: boolean; auto_run?: boolean
  marketing_profile?: MarketingProfile; runs_this_month?: number
  billing_cycle_start?: string; stripe_customer_id?: string
  stripe_subscription_id?: string; stripe_subscription_status?: string
  stripe_current_period_end?: string; stripe_trial_end?: string
  stripe_scheduled_plan?: PlanType; stripe_scheduled_plan_date?: string
  disabled_at?: string; client_count?: number
  isAdmin?: boolean
  model_provider?: string; model_id?: string; provider_api_key?: string
  modelProvider?: string | null; modelId?: string | null; providerApiKey?: string | null
}
export interface PlanLimits {
  label: string; price_usd: number; max_clients: number
  max_runs_per_month: number; max_knowledge_files_per_client: number
  max_content_types_per_run: number; max_platform_connections: number
  website_scraping: boolean; custom_compliance_prompt: boolean
  byom: boolean; auto_run: boolean; auto_publish: boolean
  analytics_retention_days: number; reports: boolean; support: string
}

// ─── Reviews / CRM ───────────────────────────────────────────────────────────────────

export interface Review {
  _id?: string; client_id: string; platform: string; review_id: string
  author_name: string; rating: number; text: string
  reply_text?: string; reply_status: ReviewReplyStatus
  fetched_at: string; replied_at?: string
}
export interface CustomerEquipment {
  type: string; start_date: string; end_date: string; status: CustomerEquipmentStatus
}
export interface Customer {
  _id?: string; client_id: string; name: string; phone: string; email: string
  equipment_rented: CustomerEquipment[]; source: string
  referral_partner_id?: string; created_at: string
}
export interface ReferralPartner {
  _id?: string; client_id: string; name: string; type: ReferralPartnerType
  contact_name: string; contact_phone: string; contact_email: string
  referral_count: number; last_referral_date?: string
  last_visit_date?: string; next_visit_date?: string; notes?: string
}
export interface Report {
  _id?: string; client_id: string; report_type: ReportType
  content: string; summary: string
  period_start: string; period_end: string; created_at: string
}
export interface AgentMemory {
  _id?: string; client_id: string; memory_type: MemoryType
  content: string; metrics?: unknown; confidence: number
  created_at: string; expires_at?: string
}

// ─── Admin ─────────────────────────────────────────────────────────────────────────────
export interface WaitlistEntry {
  docId: string; email: string; requested_at: string
}
export interface SystemPrompt {
  key: string; content: string
}

// ─── Templates ──────────────────────────────────────────────────────────────────────────
export interface ContentTemplate {
  _id: string; name: string; content_type: string; content: string
  tags?: string[]; created_at: string; updated_at?: string
}

// ─── Variants ────────────────────────────────────────────────────────────────────────────
export interface ContentVariant {
  content: string; selected?: boolean; metadata?: unknown
}
export interface VariantGroup {
  _id: string; client_id: string; content_type: string
  variants: ContentVariant[]; created_at: string
}

// ─── Calendar ────────────────────────────────────────────────────────────────────────────
export interface CalendarItem {
  _id: string; client_id: string; content_type: AnyContentType
  scheduled_for: string; status: PublishStatus; content?: string
}

// ─── Onboarding ──────────────────────────────────────────────────────────────────────────
export interface OnboardingStatus {
  hasKnowledge: boolean; hasPlatforms: boolean
  hasRun: boolean; isComplete: boolean; steps?: string[]
}

// ─── Knowledge ─────────────────────────────────────────────────────────────────────────────
export interface KnowledgeDoc {
  _id: string; client_id: string; filename: string
  content: string; chunk_count: number; created_at: string; size?: number
}

// ─── Client dashboard ────────────────────────────────────────────────────────────────
export interface ClientDashboard {
  client: ClientConfig
  latestRun?: RunSummary
  publishQueueCount: number
}

// ─── Dashboard widget types ───────────────────────────────────────────────────────────────

export interface ContentStats {
  totalGenerated: number
  totalPublished: number
  totalPendingReview: number
  emailOutreach: EmailOutreachStats
  byPlatform: PlatformPostStats[]
  platformsConnected: string[]
}

export interface EmailOutreachStats {
  generated: number
  pendingReview: number
  published: number
}

export interface PlatformPostStats {
  platform: string
  postCount: number
}

export interface EmailDayStat {
  date: string
  sent: number
  replied: number
}

export interface EmailResponseStats {
  totalSent: number
  totalReplied: number
  replyRate: number
  byDay: EmailDayStat[]
  emailConnected: boolean
}

export interface PostEngagement {
  platform: string
  likes: number
  comments: number
  shares: number
  impressions: number
}

export interface PostPerformanceStats {
  byPlatform: PostEngagement[]
  platformsConnected: string[]
}

export interface GbpLocation {
  locationId: string
  locationName: string
}

export interface GbpPlaceResult {
  placeId: string
  name: string
  address: string
  rating: number | null
  totalRatings: number | null
}

export interface GbpReviewSummary {
  averageRating: number
  totalReviews: number
  ratingDistribution: RatingBucket[]
  recentReviews: GbpReview[]
}

export interface RatingBucket {
  rating: number
  count: number
}

export interface GbpReview {
  authorName: string
  rating: number
  text: string | null
  replyStatus: string
  createdAt: string
}

export interface KeywordRanking {
  keyword: string
  position: number
  positionChange: number
  clicks: number
  impressions: number
  ctr: number
}

export interface SiteVisitSummary {
  totalSessions: number
  dailySessions: DailySession[]
}

export interface DailySession {
  date: string
  sessions: number
}

// ─── Platform testing ────────────────────────────────────────────────────────────────
export interface TestPlatformResult {
  platform: string; success: boolean; error?: string | null; message?: string
}

// ─── Client alerts ──────────────────────────────────────────────────────────────────
export interface ClientAlert {
  _id: string; client_id: string; name: string
  metric: string; threshold: number; condition: string
  channel: string; address: string; created_at: string
}

// ─── Website scraping ────────────────────────────────────────────────────────────────
export interface ScrapeWebsiteResult {
  success: boolean; chunks_added: number
}

export interface WebsiteSuggestions {
  brandName?: string | null
  brandLocation?: string | null
  brandSpecialization?: string | null
  industry?: string | null
  phone?: string | null
  businessDescription?: string | null
  servicesOffered?: string[] | null
  productCategories?: string[] | null
  serviceArea?: string | null
  facebookUrl?: string | null
  linkedinUrl?: string | null
  twitterUrl?: string | null
  ctaUrl?: string | null
}

export interface CampaignRecommendations {
  campaignGoal?: string | null
  selectedContentTypes?: string[] | null
  scheduleCadence?: string | null
  scheduleTimeUtc?: string | null
  scheduleDaysOfWeek?: number[] | null
}

// ─── Campaign run result ───────────────────────────────────────────────────────────────
export interface StartCampaignResult {
  runId: string; lgThreadId: string; lgRunId: string; status: string; engine: string
}

// ─── Queue content input ───────────────────────────────────────────────────────────────
export interface QueueContentInput {
  runId: string
  contentType: string
  content: string
  compliance: ComplianceResult
  seoMetadata?: SeoMetadata
}

// ─── Providers ─────────────────────────────────────────────────────────────────────────
export type ProviderStatus = 'new' | 'contacted' | 'engaged' | 'referred' | 'inactive'

export interface EngagementSignal {
  _id: string
  date: string
  signal_type: 'email_sent' | 'manual_contact' | 'call' | 'meeting' | 'referral'
  detail?: string
}

export interface FitScore {
  total: number
  label: string
  color: string
  talking_points: string[]
}

export interface MedicareVolume {
  total_usd: number | null
  dme_usd: number | null
  drug_usd: number | null
}

export interface DmeHcpcsItem {
  code: string
  description: string
  volume_usd: number
  claims: number
  rental: boolean
}

export interface Provider {
  _id: string
  client_id: string
  provider_name: string
  specialty: string
  practice_name: string
  city: string
  state: string
  npi: string
  email: string | null
  email_last_checked?: string
  status: ProviderStatus
  prescribing_volume_annual_usd: number | null
  estimated_total_medicare_usd?: number | null
  dme_product_mix: Array<{ __typename?: string; product: string; volume_usd: number }>
  medicare_volume?: MedicareVolume | null
  dme_hcpcs?: DmeHcpcsItem[]
  cms_rank_by_volume: number | null
  cms_no_data?: boolean
  data_year?: number | null
  cms_last_updated?: string | null
  outreach_email?: ProviderOutreachEmail | null
  phone?: string | null
  fax?: string | null
  address?: string | null
  zip?: string | null
  years_in_practice?: number | null
  ehr_system?: string | null
  multi_location?: boolean | null
  entity_type?: string | null
  medicare_type?: string | null
  ruca_category?: string | null
  total_benes?: number | null
  bene_avg_age?: number | null
  has_rental_items?: boolean | null
  website_url?: string | null
  selectedForCampaign?: boolean
  talking_points: string[]
  engagement_signals: EngagementSignal[]
  created_at: string
  updated_at: string
  __typename?: string
}

export interface ComplianceCheck {
  label: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH'
  detail: string
}

export interface OutreachComplianceResult {
  overall_severity: 'LOW' | 'MEDIUM' | 'HIGH'
  checks: ComplianceCheck[]
}

export interface OutreachEmailDraft {
  subject: string
  body: string
  email: string
}

export interface ProviderOutreachEmail {
  subject: string
  body: string
  sent_at?: string | null
}

export interface DiscoverySummary {
  total_found: number
  new_results: number
  already_in_pipeline: number
}

export interface OutreachRun {
  runId: string
  status: 'running' | 'complete' | 'error'
  email: OutreachEmailDraft | null
  nodeRuntimeData: unknown
}

export type EmailStyleOverride = 'automatic' | 'cold' | 'consultative'
export type FrameworkOverride = 'automatic' | 'TIA' | 'OPPA' | 'QVA'
export type PsychologyStrategy = 'automatic' | 'loss_aversion' | 'expertise' | 'value_first' | 'partnership'

export interface OutreachOverrides {
  emailStyleOverride?: EmailStyleOverride
  frameworkOverride?: FrameworkOverride
  psychologyStrategy?: PsychologyStrategy
}

export interface ProvenanceClaim {
  text: string
  source: string
  field: string
  actualValue: string
  verified: boolean
}

export interface ProvenanceSentence {
  sentence: string
  claims: ProvenanceClaim[]
}

// ─── Pipeline Configuration ───────────────────────────────────────────────────────────────

export interface ClientPipelineConfig {
  pipeline: string
  config: Record<string, unknown>
  updatedAt: string
}

export interface PipelinePrompts {
  generate_system?: string
  validate_system?: string
}

// ─── Outreach Queue ───────────────────────────────────────────────────────────────────────────

export interface OutreachQueueItem {
  id: string
  clientId: string
  providerNpi: string
  providerName: string
  providerPractice: string
  subject: string
  body: string
  qualityScore: number
  icpScore: number
  icpTier: string
  status: string
  sequenceId: string
  sequencePosition: number
  nextFollowupAt: string | null
  psychologyStrategy: string
  discoverySource: string
  createdAt: string
  sentAt: string | null
  approvedAt: string | null
}

// ─── AI Dashboard Insights ────────────────────────────────────────────────────────────────

export interface InsightBullet {
  signal: 'up' | 'alert' | 'action' | 'opportunity'
  label: string
  detail: string
}

export interface PerWidgetInsights {
  siteVisits: InsightBullet[]
  emailOutreach: InsightBullet[]
  emailResponses: InsightBullet[]
  socialPerformance: InsightBullet[]
  gbpReviews: InsightBullet[]
  keywordRankings: InsightBullet[]
  contentOverview: InsightBullet[]
}

export interface DashboardInsights {
  generatedAt: string
  summary: InsightBullet[]
  perWidget: PerWidgetInsights
}

// ─── Email Campaigns ─────────────────────────────────────────────────────────────────

export type EmailCampaignStatus = 'draft' | 'in_progress' | 'sending' | 'sent' | 'partial_failure'
export type EmailCampaignProviderStatus = 'pending' | 'sent' | 'failed' | 'replied' | 'needs_followup'

export interface EmailCampaignProvider {
  providerId: string
  providerNpi: string
  providerName: string
  email: string
  status: EmailCampaignProviderStatus
  gmailThreadId?: string | null
  sentAt?: string | null
  repliedAt?: string | null
  replyBody?: string | null
  error?: string | null
}

export interface ReviewIssue {
  category: string
  description: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH'
}

export interface EmailReviewResult {
  overallSeverity: 'LOW' | 'MEDIUM' | 'HIGH'
  legalIssues: ReviewIssue[]
  marketingIssues: ReviewIssue[]
  suggestions: string[]
}

export interface GmailQuotaInfo {
  sentCount: number
  dailyLimit: number
  canSend: boolean
  warningMessage: string | null
}

export interface EmailCampaign {
  id: string
  clientId: string
  name: string
  subject: string
  body: string
  status: EmailCampaignStatus
  providers: EmailCampaignProvider[]
  sentCount: number
  repliedCount: number
  failedCount: number
  aiReview?: EmailReviewResult | null
  createdAt: string
  updatedAt: string
  sentAt?: string | null
}
